import React, { useState, useEffect, useCallback } from 'react'
import { Card, Table, Button, Modal, Input, message, Tag, Typography, Space, Popconfirm, Badge } from 'antd'
import { EyeOutlined, MessageOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { backend } from '../../api'

const { TextArea } = Input
const { Text, Paragraph } = Typography

const STATUS_MAP = {
  open: { color: 'blue', text: '待处理' },
  pending: { color: 'orange', text: '待回复' },
  replied: { color: 'green', text: '已回复' },
  closed: { color: 'default', text: '已关闭' }
}

export default function TicketManage() {
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [currentTicket, setCurrentTicket] = useState(null)
  const [replyContent, setReplyContent] = useState('')
  const [replying, setReplying] = useState(false)

  const loadTickets = useCallback(async () => {
    setLoading(true)
    try {
      const res = await backend.adminGetTickets()
      if (res.success) setTickets(res.tickets || [])
    } catch (e) { message.error('加载失败') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { loadTickets() }, [loadTickets])

  const handleReply = async () => {
    if (!replyContent.trim()) { message.warning('请输入回复内容'); return }
    if (!currentTicket) return
    setReplying(true)
    try {
      const res = await backend.adminReplyTicket(currentTicket.id, replyContent.trim())
      if (res.success) {
        message.success('回复已发送')
        setReplyContent('')
        setCurrentTicket(res.ticket || { ...currentTicket, status: 'replied', replies: [...(currentTicket.replies || []), { content: replyContent.trim(), from_admin: true, created_at: new Date().toISOString() }] })
        loadTickets()
      } else {
        message.error(res.message || '回复失败')
      }
    } catch (e) { message.error('回复失败') }
    finally { setReplying(false) }
  }

  const handleClose = async (id) => {
    try {
      const res = await backend.adminCloseTicket(id)
      if (res.success) { message.success('工单已关闭'); loadTickets() }
      else { message.error(res.message || '操作失败') }
    } catch (e) { message.error('操作失败') }
  }

  const openDetail = (record) => {
    setCurrentTicket(record)
    setDetailOpen(true)
  }

  const columns = [
    { title: '标题', dataIndex: 'title', key: 'title', width: 200 },
    { title: '内容', dataIndex: 'content', key: 'content', ellipsis: true },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: s => {
        const cfg = STATUS_MAP[s] || { color: 'default', text: s || '未知' }
        return <Tag color={cfg.color}>{cfg.text}</Tag>
      }
    },
    {
      title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160,
      render: v => v ? new Date(v).toLocaleString('zh-CN') : '-'
    },
    {
      title: '操作', key: 'actions', width: 160,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)}>查看</Button>
          {record.status !== 'closed' && (
            <Popconfirm title="确定关闭此工单?" onConfirm={() => handleClose(record.id)}>
              <Button type="link" size="small" danger>关闭</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ]

  const unreadCount = tickets.filter(t => t.status === 'open' || t.status === 'pending').length

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          工单管理
          {unreadCount > 0 && <Badge count={unreadCount} style={{ marginLeft: 8 }} />}
        </Typography.Title>
        <Button onClick={loadTickets}>刷新</Button>
      </div>

      <Card>
        <Table dataSource={tickets} columns={columns} rowKey="id" loading={loading}
          pagination={{ pageSize: 10 }} size="small" />
      </Card>

      <Modal title="工单详情" open={detailOpen} onCancel={() => setDetailOpen(false)}
        footer={currentTicket?.status !== 'closed' ? [
          <Button key="close" danger onClick={() => { handleClose(currentTicket?.id); setDetailOpen(false) }}>关闭工单</Button>,
          <Button key="cancel" onClick={() => setDetailOpen(false)}>取消</Button>
        ] : [
          <Button key="cancel" onClick={() => setDetailOpen(false)}>关闭</Button>
        ]}
        width={600}
      >
        {currentTicket && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>标题：</Text><Text>{currentTicket.title}</Text>
              <Tag color={STATUS_MAP[currentTicket.status]?.color || 'default'} style={{ marginLeft: 8 }}>
                {STATUS_MAP[currentTicket.status]?.text || currentTicket.status}
              </Tag>
            </div>
            <Card size="small" style={{ marginBottom: 12, background: '#f6f8fa' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>用户提交</Text>
              <Paragraph style={{ marginBottom: 0, marginTop: 4 }}>{currentTicket.content}</Paragraph>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {currentTicket.created_at ? new Date(currentTicket.created_at).toLocaleString('zh-CN') : ''}
              </Text>
            </Card>

            {(currentTicket.replies || []).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>回复记录</Text>
                {(currentTicket.replies || []).map((r, i) => (
                  <Card key={i} size="small" style={{ marginTop: 8, background: r.from_admin ? '#e6f7ff' : '#f6f8fa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <Tag color={r.from_admin ? 'blue' : 'default'}>{r.from_admin ? '管理员' : '用户'}</Tag>
                      <Text type="secondary" style={{ fontSize: 11 }}>{r.created_at ? new Date(r.created_at).toLocaleString('zh-CN') : ''}</Text>
                    </div>
                    <Paragraph style={{ marginBottom: 0 }}>{r.content}</Paragraph>
                  </Card>
                ))}
              </div>
            )}

            {currentTicket.status !== 'closed' && (
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>回复</Text>
                <TextArea rows={3} value={replyContent} onChange={e => setReplyContent(e.target.value)}
                  placeholder="输入回复内容..." />
                <div style={{ marginTop: 8, textAlign: 'right' }}>
                  <Button type="primary" icon={<MessageOutlined />} onClick={handleReply} loading={replying}>
                    发送回复
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}
